/**
 * Image-space geometry for the seen-area tracker. Image pixels are full resolution, y down, as in the
 * OSD TiledImage and the backend tissue builder. Rects are [x0, y0, x1, y1].
 */
import {stitchedLevelGrid} from './seenAreaFormat';

/** Candidates are bounded so a bad extent can never stall a tick. */
export const MAX_CANDIDATES = 4000;

export const polygonArea = (points) => {
    let area = 0;
    for (let i = 0; i < points.length; i++) {
        const [x0, y0] = points[i];
        const [x1, y1] = points[(i + 1) % points.length];
        area += x0 * y1 - x1 * y0;
    }
    return Math.abs(area) / 2;
};

const clipEdge = (points, inside, intersect) => {
    const out = [];
    for (let i = 0; i < points.length; i++) {
        const current = points[i];
        const previous = points[(i + points.length - 1) % points.length];
        const currentIn = inside(current);
        if (currentIn !== inside(previous)) out.push(intersect(previous, current));
        if (currentIn) out.push(current);
    }
    return out;
};

const lerpAtX = (a, b, x) => [x, a[1] + (b[1] - a[1]) * (x - a[0]) / (b[0] - a[0])];
const lerpAtY = (a, b, y) => [a[0] + (b[0] - a[0]) * (y - a[1]) / (b[1] - a[1]), y];

/** Sutherland–Hodgman clip of a convex polygon to an axis-aligned rect. */
export const clipPolygonToRect = (points, [x0, y0, x1, y1]) => {
    let clipped = points;
    clipped = clipEdge(clipped, p => p[0] >= x0, (a, b) => lerpAtX(a, b, x0));
    if (clipped.length) clipped = clipEdge(clipped, p => p[0] <= x1, (a, b) => lerpAtX(a, b, x1));
    if (clipped.length) clipped = clipEdge(clipped, p => p[1] >= y0, (a, b) => lerpAtY(a, b, y0));
    if (clipped.length) clipped = clipEdge(clipped, p => p[1] <= y1, (a, b) => lerpAtY(a, b, y1));
    return clipped;
};

export const rectArea = ([x0, y0, x1, y1]) => Math.max(0, x1 - x0) * Math.max(0, y1 - y0);

/** Share of `rect` inside the (convex) viewport polygon. */
export const visibleFraction = (polygon, rect) => {
    const area = rectArea(rect);
    if (area <= 0) return 0;
    const clipped = clipPolygonToRect(polygon, rect);
    return clipped.length < 3 ? 0 : polygonArea(clipped) / area;
};

export const boundingBox = (points) => {
    const xs = points.map(p => p[0]);
    const ys = points.map(p => p[1]);
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
};

/**
 * Stitched tiles of `level` overlapping `bbox`, each clipped to the image. Tile (x, y) covers
 * tileSize * 2^(z_max - level) full-resolution pixels, as in the stitch manifest.
 */
export const stitchedTilesInBox = (grid, level, tileSize, imageSize, bbox) => {
    const {cols, rows} = stitchedLevelGrid(grid, level);
    const scale = Math.pow(2, grid.z_max - level);
    const tw = tileSize.width * scale;
    const th = tileSize.height * scale;
    const x0 = Math.max(0, Math.floor(bbox[0] / tw));
    const y0 = Math.max(0, Math.floor(bbox[1] / th));
    const x1 = Math.min(cols - 1, Math.floor(bbox[2] / tw));
    const y1 = Math.min(rows - 1, Math.floor(bbox[3] / th));
    const tiles = [];
    if (x1 < x0 || y1 < y0 || (x1 - x0 + 1) * (y1 - y0 + 1) > MAX_CANDIDATES) return tiles;
    for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
            tiles.push({
                idx: y * cols + x,
                rect: [x * tw, y * th, Math.min((x + 1) * tw, imageSize.width), Math.min((y + 1) * th, imageSize.height)],
            });
        }
    }
    return tiles;
};

/** Camera fields from `txty` as {idx: j * x_fields + i, rect}; field size is the full-resolution tile size. */
export const getFieldRects = (txty, fieldSize, xFields) => {
    const fields = [];
    for (const name in txty || {}) {
        const info = txty[name];
        const x = info?.pos?.X;
        const y = info?.pos?.Y;
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        fields.push({
            idx: info.y_index * xFields + info.x_index,
            rect: [x, y, x + fieldSize.width, y + fieldSize.height],
        });
    }
    return fields;
};

export const fieldsInBox = (fields, bbox) => {
    const hits = fields.filter(({rect}) => rect[2] > bbox[0] && rect[0] < bbox[2] && rect[3] > bbox[1] && rect[1] < bbox[3]);
    return hits.length > MAX_CANDIDATES ? [] : hits;
};
